import {
  checkEmployeeDirectoryFile,
  fileToBase64,
} from '../../../helpers/employeeDirectoryFile';
import type { AgentFormData } from '../../../interfaces/Agent';
import { KnowledgeFileData } from '../../../interfaces/Knowledge';
import type { TranslationKey } from '../../../types/i18n';

// The Knowledge step's limits: MAX_FILES, and FilePond's maxFileSize="20MB",
// which FilePond counts in base 1000.
export const MAX_KNOWLEDGE_FILES = 250;
const MAX_KNOWLEDGE_FILE_BYTES = 20 * 1000 * 1000;
const KNOWLEDGE_EXTENSIONS = ['.pdf', '.csv'];

export type KnowledgeFileRejectionReason =
  | 'type'
  | 'size'
  | 'duplicate'
  | 'limit'
  | 'read';

export interface RejectedKnowledgeFile {
  name: string;
  reason: KnowledgeFileRejectionReason;
}

/** Files the assistant handed over, as the Knowledge step would hold them. */
export const acceptAssistantKnowledgeFiles = async (
  files: File[],
  existing: KnowledgeFileData[]
): Promise<{
  accepted: KnowledgeFileData[];
  rejected: RejectedKnowledgeFile[];
}> => {
  const names = new Set(existing.map(file => file.name));
  const accepted: KnowledgeFileData[] = [];
  const rejected: RejectedKnowledgeFile[] = [];

  for (const file of files) {
    const isKnowledgeType = KNOWLEDGE_EXTENSIONS.some(extension =>
      file.name.toLowerCase().endsWith(extension)
    );
    let reason: KnowledgeFileRejectionReason | null = null;
    if (!isKnowledgeType) reason = 'type';
    else if (file.size > MAX_KNOWLEDGE_FILE_BYTES) reason = 'size';
    else if (names.has(file.name)) reason = 'duplicate';
    else if (names.size >= MAX_KNOWLEDGE_FILES) reason = 'limit';

    if (reason) {
      rejected.push({ name: file.name, reason });
      continue;
    }
    try {
      accepted.push({ name: file.name, content: await fileToBase64(file) });
      names.add(file.name);
    } catch {
      rejected.push({ name: file.name, reason: 'read' });
    }
  }

  return { accepted, rejected };
};

/**
 * Adds encoded files to the current list, checking names and the total again:
 * the list may have grown while they were encoding. Pure, so it's safe to call
 * from a state updater. Returns `current` itself when nothing was added.
 */
export const addKnowledgeFiles = (
  current: KnowledgeFileData[],
  accepted: KnowledgeFileData[]
): { files: KnowledgeFileData[]; rejected: RejectedKnowledgeFile[] } => {
  const names = new Set(current.map(file => file.name));
  const added: KnowledgeFileData[] = [];
  const rejected: RejectedKnowledgeFile[] = [];

  for (const file of accepted) {
    if (names.has(file.name)) {
      rejected.push({ name: file.name, reason: 'duplicate' });
    } else if (names.size >= MAX_KNOWLEDGE_FILES) {
      rejected.push({ name: file.name, reason: 'limit' });
    } else {
      names.add(file.name);
      added.push(file);
    }
  }

  return {
    files: added.length > 0 ? [...current, ...added] : current,
    rejected,
  };
};

/** A directory the assistant handed over, staged as the Capabilities step would. */
export const acceptAssistantEmployeeDirectory = async (
  file: File
): Promise<
  | {
      ok: true;
      stagedFile: NonNullable<AgentFormData['employee_directory_staged_file']>;
    }
  | { ok: false; error: TranslationKey }
> => {
  const check = await checkEmployeeDirectoryFile(file, { staged: true });
  if (!check.ok) return check;

  try {
    return {
      ok: true,
      stagedFile: {
        fileName: check.file.name,
        base64: await fileToBase64(check.file),
      },
    };
  } catch {
    return {
      ok: false,
      error: 'The file could not be read. Please try again.',
    };
  }
};
